import { useState, useRef, useCallback, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FounderContext, ValidatorContext } from '@/App';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import IntakeForm from '@/components/IntakeForm';
import PipelineProgress from '@/components/PipelineProgress';
import ClinicalOutput from '@/components/ClinicalOutput';
import PPHVSection from '@/components/PPHVSection';
import CrowStory from '@/components/CrowStory';
import EarlyAccess from '@/components/EarlyAccess';
import FounderAdmin from '@/components/FounderAdmin';
import AgentChat from '@/components/AgentChat';
import InvestorSuite from '@/components/InvestorSuite';
import AmbientScribe from '@/components/AmbientScribe';
import DemoSummaryModal from '@/components/DemoSummaryModal';
import ValidatorFeedbackPopup from '@/components/ValidatorFeedbackPopup';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, AlertTriangle, CheckCircle, Zap, ShieldCheck, HeartPulse, ArrowDown, Settings, Mic, Heart, Lock, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STEPS = [
  { step: 1, name: 'Intake Agent', desc: 'Validating and structuring intake data' },
  { step: 2, name: 'Redactor Agent', desc: 'Sanitizing sensitive patterns' },
  { step: 3, name: 'Normalizer Agent', desc: 'Normalizing medical terminology' },
  { step: 4, name: 'Longitudinal Memory Agent', desc: 'Checking prior encounter history' },
  { step: 5, name: 'Draft Clinical Note Agent', desc: 'Generating SOAP note' },
  { step: 6, name: 'Guardrails Agent', desc: 'Running safety checks' },
  { step: 7, name: 'Audit Log Agent', desc: 'Creating audit trail' },
];

export default function HomePage() {
  const [result, setResult] = useState(null);
  // Validator flow — identity captured at registration (before intake); the optional
  // survey reuses it and never re-asks. surveyOpen is triggered by "Save Comment".
  const [validatorIdentity, setValidatorIdentity] = useState({ name: '', email: '', specialty: '' });
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [viewMode, setViewMode] = useState('physician');
  const [showRetrieve, setShowRetrieve] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showArchitecture, setShowArchitecture] = useState(false);
  const [retrieveId, setRetrieveId] = useState('');
  const [retrievedReport, setRetrievedReport] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [retrieveLoading, setRetrieveLoading] = useState(false);
  const [showAgent, setShowAgent] = useState(false);
  const [showInvestor, setShowInvestor] = useState(false);
  const [showFounderPin, setShowFounderPin] = useState(false);
  const [founderPin, setFounderPin] = useState('');
  const [showAmbientScribe, setShowAmbientScribe] = useState(false);
  const [showDemoSummary, setShowDemoSummary] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editEncounterId, setEditEncounterId] = useState('');
  const [editInitialData, setEditInitialData] = useState(null);
  const [showClassificationModal, setShowClassificationModal] = useState(false);
  const [pendingClassificationData, setPendingClassificationData] = useState(null);
  const [transcriptConfirmed, setTranscriptConfirmed] = useState('YES'); // YES | NO
  // ─── Live build status — fetched from /api/system/build-status so the
  // Company Brief accordion numbers stay in lockstep with the live engine
  // count. June 3, 2026 — honest defaults post data-integrity audit.
  // founder_team_self_test_encounters is the source=user count AFTER synthetic-marker
  // cleanup (Task 2). Platform is pre-pilot until first real patient signs in.
  // NO LLM. Falls back to baked-in defaults on error.
  const [buildStatus, setBuildStatus] = useState({
    engines: 218,
    catalog_engines: 307,
    regression_pass: 2106,
    regression_total: 2110,
    synthetic_critical: '52/52',
    synthetic_negation: '15/15',
    synthetic_cross_engine: '6/6',
    synthetic_priority_filter: '3/3',
    safety_stack_layers: 8,
    patent_claims: 54,
    languages: 25,
    channels: ['web', 'voice', 'telegram', 'whatsapp', 'ambient_scribe'],
    users: 3,
    founder_team_self_test_encounters: 108,
    counter: 108,
    yc_demo_encounters: 411,
    ambient_scribe_encounters: 157,
    system_test_encounters: 23375,
    encounters: 244,
    registrations: 2,
    last_updated: '2026-07-04',
  });
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/system/build-status`).then(r => {
      if (!cancelled && r && r.data && typeof r.data.engines === 'number') {
        setBuildStatus(prev => ({ ...prev, ...r.data }));
      }
    }).catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);
  const { isFounder, activateFounder } = useContext(FounderContext);
  const { isValidator } = useContext(ValidatorContext);

  const intakeRef = useRef(null);
  const resultsRef = useRef(null);

  const scrollToIntake = useCallback(() => {
    setResult(null);
    setCompletedSteps([]);
    intakeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleSubmit = useCallback(async (formData, mode) => {
    setIsProcessing(true);
    setResult(null);
    setCompletedSteps([]);
    setViewMode(mode || 'physician');
    if (formData && (formData.validatorName || formData.validatorEmail || formData.validatorSpecialty)) {
      setValidatorIdentity({
        name: formData.validatorName || '',
        email: formData.validatorEmail || '',
        specialty: formData.validatorSpecialty || '',
      });
    }
    let stepIdx = 0;
    const interval = setInterval(() => {
      if (stepIdx < PIPELINE_STEPS.length) {
        setCompletedSteps(prev => [...prev, PIPELINE_STEPS[stepIdx].name]);
        stepIdx++;
      }
    }, 400);
    try {
      let response;
      if (editMode && editEncounterId) {
        const passcode = window.prompt('Enter admin passcode to save edits:');
        if (!passcode) {
          clearInterval(interval);
          setIsProcessing(false);
          setCompletedSteps([]);
          return;
        }
        response = await axios.patch(`${API}/encounter/${editEncounterId}/edit`, { ...formData, adminPasscode: passcode });
        toast.success(`Encounter ${editEncounterId} updated`);
      } else {
        response = await axios.post(`${API}/intake`, formData);
      }
      clearInterval(interval);

      // Rule D — physician decision required: pause processing, show modal, wait for confirmation
      if (response.data && response.data.requiresPhysicianClassification) {
        setIsProcessing(false);
        setCompletedSteps([]);
        setPendingClassificationData({ formData, mode, classificationBox: response.data.classificationBox });
        setShowClassificationModal(true);
        return;
      }

      setCompletedSteps(PIPELINE_STEPS.map(s => s.name));
      setTimeout(() => {
        setResult(response.data);
        setIsProcessing(false);
        setEditMode(false);
        setEditEncounterId('');
        setEditInitialData(null);
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
      }, 600);
    } catch (error) {
      clearInterval(interval);
      setIsProcessing(false);
      // UI cache fix: clear stale result + completed steps on submit failure (mobile network hiccup safety)
      setResult(null);
      setCompletedSteps([]);
      setEditMode(false);
      setEditEncounterId('');
      setEditInitialData(null);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Submit failed. Please check your connection and try again.');
    }
  }, [editMode, editEncounterId]);

  const handleEditIntake = useCallback(async (encounterId) => {
    try {
      const res = await axios.get(`${API}/encounter/${encounterId}`);
      const enc = res.data;
      setEditInitialData({
        age: enc.age || '', sex: enc.sex || '', language: enc.language || 'English',
        chiefComplaint: enc.chiefComplaint || '', symptoms: enc.symptoms || '',
        medications: enc.medications || '', existingConditions: enc.existingConditions || '',
        notes: enc.notes || '', communicationMode: enc.communicationMode || 'text',
        caregiverAssisted: !!enc.caregiverAssisted, consentGiven: true,
        contactName: enc.contactName || '', contactEmail: enc.contactEmail || '',
        contactRole: enc.contactRole || '', contactFeedback: enc.contactFeedback || '',
      });
      setEditEncounterId(encounterId);
      setEditMode(true);
      setResult(null);
      setCompletedSteps([]);
      setTimeout(() => intakeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      toast.info(`Editing ${encounterId}. Update fields and save.`);
    } catch (err) {
      toast.error('Failed to load encounter for editing');
    }
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
    setEditEncounterId('');
    setEditInitialData(null);
    toast.info('Edit cancelled');
  }, []);

  const handleRetrieve = useCallback(async () => {
    if (!retrieveId.trim()) return;
    setRetrieveLoading(true);
    try {
      const res = await axios.get(`${API}/encounter/${retrieveId.trim()}`);
      setRetrievedReport(res.data);
    } catch { toast.error('Encounter not found.'); setRetrievedReport(null); }
    finally { setRetrieveLoading(false); }
  }, [retrieveId]);

  const handleOpenHistory = useCallback(async () => {
    setShowHistory(true);
    try {
      const res = await axios.get(`${API}/encounters/history`);
      setHistoryData(res.data.encounters || []);
    } catch { toast.error('Failed to load history'); }
  }, []);

  const handleOpenSoap = useCallback(() => {
    if (result) { resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    else { setShowRetrieve(true); }
  }, [result]);

  const handleAmbientResult = useCallback((data) => {
    setResult(data);
    setViewMode('physician');
    setShowAmbientScribe(false);
    setCompletedSteps(PIPELINE_STEPS.map(s => s.name));
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    toast.success(`Ambient scribe completed: ${data.encounterId}`);
  }, []);

  return (
    <div className="min-h-screen bg-white scroll-smooth">
      <Header
        onRetrieve={() => { setShowRetrieve(true); setRetrievedReport(null); setRetrieveId(''); }}
        onSoap={handleOpenSoap}
        onStartIntake={scrollToIntake}
        onHistory={handleOpenHistory}
        onAdmin={() => setShowAdmin(true)}
        onAgent={() => setShowAgent(true)}
        onInvestor={() => setShowInvestor(true)}
        onAmbientScribe={() => setShowAmbientScribe(true)}
        onDemoSummary={() => setShowDemoSummary(true)}
      />

      {/* Hero — full width flush under header */}
      <HeroSection />

      {/* Two-column card: Copy + Disclaimer (matches Replit) */}
      <section className="max-w-5xl mx-auto px-6 py-8" data-testid="hero-copy-section">
        <div className="border-2 border-slate-400 rounded-xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
          {/* Left: Copy */}
          <div className="p-6 md:p-8 border-b-2 md:border-b-0 md:border-r-2 border-slate-400">
            <p className="text-base font-bold text-black mb-3">
              Patients don't speak medical language — they just describe how they feel.
            </p>
            <p className="text-sm text-black mb-3">
              Important details can get missed before the doctor visit even begins.
            </p>
            <p className="text-sm text-black mb-3">
              <span className="text-[#2563EB] font-semibold">P.AI Global Health (Patient AI)</span> turns what patients say into clear medical notes before they see the doctor.
            </p>
            <p className="text-sm text-black">
              Every doctor sees your health history as structured clinical notes (SOAP). Your health record stays with you — securely stored in your personal health vault.
            </p>
          </div>
          {/* Right: Disclaimer + Value Cues */}
          <div className="p-6 md:p-8">
            <p className="text-xs font-bold text-red-600 tracking-wide uppercase mb-2">DEMO PROTOTYPE — READ BEFORE USE</p>
            <p className="text-sm text-black mb-4">
              Clinical summaries and SOAP notes are generated by deterministic clinical engines citing named published guidelines — not AI or large language models. They illustrate the intake-to-documentation process and are not diagnoses or treatment plans. All outputs require clinician review. Clinical accuracy and reasoning will be continuously refined in production.
            </p>
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-black">→ Fewer errors</p>
              <p className="text-sm font-semibold text-black">→ Less confusion</p>
              <p className="text-sm font-semibold text-black">→ Fewer missed red flags</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── All synthetic/test/engine/encounter count surfacing REMOVED ──────
          Per founder directive (truth-lock §9.6): no engine count, no pytest
          count, no LLM count, and no encounter count appears on any public
          or authenticated patient/investor-facing surface. The admin dashboard
          retains its counter at /admin/audit-trust; nothing else surfaces
          these numbers anywhere. The BuildStatusRibbon component was deleted
          on 2026-06-08 as part of the same enforcement. */}

      {/* Patient-Empowered Global Healthcare */}
      <section className="max-w-5xl mx-auto px-6 py-10" data-testid="patient-empowered">
        {/* Interoperability anchor (May 17, 2026) — appears at the top of the hero block */}
        {/* Session 4 (Part J) — "True Interoperability" anchor moved to the footer
            "Information & Resources" section (id="footer-interoperability"). */}

        <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-black mb-2">
          Patient-Empowered <span className="text-[#2563EB] italic">Global Healthcare</span>
        </h2>
        <p className="text-lg font-bold text-black mb-2">
          Your health history should belong to <span className="text-[#2563EB]">you</span> — not to a hospital database.
        </p>
        <p className="text-sm text-black mb-2">
          Patient-owned health vault that turns medical history into clinician-ready intelligence in seconds.
        </p>
        <p className="text-sm text-black mb-5">
          Speak your health story in your own language. <strong>P.AI Global Health (Patient AI)</strong> turns what you share into a clear health summary and a clinical report your doctor can use — securely and instantly.
        </p>

        <div className="flex flex-wrap gap-3 mb-6">
          {[
            { icon: <Mic className="w-3.5 h-3.5" />, label: 'Voice intake' },
            { icon: <Heart className="w-3.5 h-3.5" />, label: 'Patient-first design' },
            { icon: <Lock className="w-3.5 h-3.5" />, label: 'Secure & private' },
          ].map((pill, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-black" data-testid={`feature-pill-${i}`}>{pill.icon}{pill.label}</span>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={scrollToIntake} className="rounded-md bg-[#2563EB] hover:bg-blue-700 text-white text-sm px-5 py-2.5" data-testid="hero-start-intake-btn">
            <ArrowDown className="w-4 h-4 mr-2" /> Start My Intake
          </Button>
        </div>
      </section>

      {/* Intake Section */}
      <div ref={intakeRef} className="scroll-mt-16">
        {/* Session 4 (Part J) — AI-terminology / instructions / physician-framing /
            one-pager accordions moved to the footer "Information & Resources" section. */}

        {/* Dear Doctor letter — prominent stable double-lined box (May 21, 2026).
            Rendered ONLY on /register/validator so Archna, Sajan, Sharon, Sujatha, Seetal
            and any future clinical validator sees the founder's invitation as the first
            substantial content above the registration form. */}
        {isValidator && (
          <section className="max-w-5xl mx-auto px-6 pt-3 pb-2" data-testid="dear-doctor-section">
            <div
              className="rounded-lg border-double border-4 border-slate-800 bg-white px-6 py-5 shadow-sm"
              data-testid="dear-doctor-box"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              <p className="text-sm sm:text-base text-slate-900 leading-relaxed">Dear Doctor,</p>
              <p className="text-sm sm:text-base text-slate-800 leading-relaxed mt-2.5">
                I'd like your clinical validation of SOAP notes on a new deterministic Clinical Decision Support layer designed for physician review during physician triage. Not HIPAA-compliant. This is a synthetic-data sandbox. The production architecture is designed to be HIPAA-Eligible.
              </p>
              <p className="text-sm sm:text-base text-slate-800 leading-relaxed mt-2.5">
                The architecture mirrors Epic Best Practice Advisories and Cerner rules — but every rule is deterministic Python, audit-trail per flag, no large language model anywhere in the decision loop.
              </p>
              <p className="text-sm sm:text-base text-slate-800 leading-relaxed mt-2.5">
                Dr. V. N. Devarajan (Internal Medicine + Nephrology, 44 years' practice) has already reviewed 18 cases. I'd be honored to have your eyes on a similar set scoped to your specialty.
              </p>
              <p className="text-sm sm:text-base text-slate-800 leading-relaxed mt-2.5">
                Patent Pending. Your feedback shapes the next iteration. Thank you for considering.
              </p>
              <p className="text-sm sm:text-base font-bold text-slate-900 leading-relaxed mt-3">
                Sivam (Sadasivam Eniasivam) · P.Ai Global Health LLC
              </p>
            </div>
          </section>
        )}
        <IntakeForm
          onSubmit={handleSubmit}
          isProcessing={isProcessing}
          onStartNew={() => { setResult(null); setCompletedSteps([]); }}
          editMode={editMode}
          editEncounterId={editEncounterId}
          initialData={editInitialData}
          onCancelEdit={handleCancelEdit}
        />
      </div>

      {/* Pipeline Progress */}
      {(isProcessing || completedSteps.length > 0) && !result && (
        <PipelineProgress steps={PIPELINE_STEPS} completedSteps={completedSteps} />
      )}

      {/* Results */}
      {result && (
        <div ref={resultsRef} className="scroll-mt-16">
          <ClinicalOutput
            result={result}
            viewMode={viewMode}
            onEdit={handleEditIntake}
            onViewPatient={() => {
              setViewMode('patient');
              setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
          />
        </div>
      )}

      {/* Optional post-SOAP survey — opens right after the validator clicks "Save
          Comment". Identity captured at registration; survey has no required fields. */}
      {isValidator && result?.encounterId && (
        <ValidatorFeedbackPopup
          encounterId={result.encounterId}
          open={surveyOpen}
          onClose={() => setSurveyOpen(false)}
          validatorNameHint={validatorIdentity.name}
          validatorEmailHint={validatorIdentity.email}
          validatorSpecialtyHint={validatorIdentity.specialty}
        />
      )}

      <EarlyAccess currentEncounterId={result?.encounterId} key={result?.encounterId || 'new'} onCommentSaved={() => { if (isValidator) setSurveyOpen(true); }} />
      {/* Crow parable — relocated directly below the Register Interest form (UI reorder) */}
      <CrowStory />

      {/* Disclaimer */}
      <section className="max-w-4xl mx-auto px-6 py-6">
        <h4 className="font-bold text-base text-black mb-2">Disclaimer</h4>
        <p className="text-sm text-black">Demo uses synthetic patient data. No real patient data is required.</p>
      </section>

      {/* Footer */}
      {/* ─── Session 4 (Part J) · Information & Resources — moved below the fold so the
          intake form surfaces sooner. Header "How to Use ?" links target these footer ids. ─── */}
      <section className="max-w-5xl mx-auto px-6 py-8" data-testid="footer-info-section">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Information &amp; Resources</h3>
        <div className="space-y-3">
          {/* footer-interoperability */}
          <details id="footer-interoperability" className="group bg-slate-50 border-2 border-slate-300 rounded-2xl overflow-hidden">
            <summary className="cursor-pointer list-none px-5 py-3 flex items-center justify-between bg-white hover:bg-slate-50 select-none">
              <span className="text-sm sm:text-base font-bold text-[#0369A1]">
                <span className="inline-block w-5 h-5 rounded-full bg-[#0369A1] text-white text-[11px] font-bold text-center leading-5 mr-2">i</span>
                True Interoperability
              </span>
              <span className="text-[#0369A1] group-open:rotate-180 transition-transform text-lg">▾</span>
            </summary>
            <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-2 border-t border-slate-200">
              <h4 className="text-lg font-bold text-black leading-snug">Patient Health Data from Different Sources — Organized for True Interoperability</h4>
              <p className="text-sm text-black italic">It travels with the patient anywhere.</p>
              <p className="text-xs text-slate-600 italic"><strong>Not HIPAA-compliant.</strong> This is a synthetic-data sandbox. The production architecture is designed to be HIPAA-Eligible.</p>
            </div>
          </details>
        {/* AI Terminology Explainer (May 17, 2026) — collapsible accordion above the form.
            Always visible to every viewer regardless of patient/physician toggle. */}
        <section className="max-w-5xl mx-auto px-6 pt-4 pb-2" data-testid="ai-terminology-section">
          <details id="footer-hallucinate" className="group bg-slate-50 border-2 border-slate-300 rounded-2xl overflow-hidden">
            <summary
              className="cursor-pointer list-none px-5 py-3 flex items-center justify-between bg-white hover:bg-slate-50 select-none"
              data-testid="ai-terminology-toggle"
            >
              <span className="text-sm sm:text-base font-bold text-[#0369A1]">
                <span className="inline-block w-5 h-5 rounded-full bg-[#0369A1] text-white text-[11px] font-bold text-center leading-5 mr-2">i</span>
                Why P.AI Global Health (Patient AI) doesn't hallucinate
                <span className="text-xs font-normal text-slate-500 ml-2">(tap for the AI terminology explainer)</span>
              </span>
              <span className="text-[#0369A1] group-open:rotate-180 transition-transform text-lg">▾</span>
            </summary>
            <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-3 border-t border-slate-200" data-testid="ai-terminology-body">
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">What is "hallucination" in clinical AI?</p>
                <p className="text-sm text-slate-700">
                  When a large language model (LLM) generates text, it predicts the next word based on patterns in its training data. It does not consult a fixed knowledge base. When it does not know an answer, it invents one that <em>sounds</em> plausible. This is called hallucination — fabricated content delivered with the same fluent confidence as accurate content.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Why is this dangerous in a clinical setting?</p>
                <p className="text-sm text-slate-700">
                  NBC News recently reported on <strong>OpenEvidence</strong>, a $12 billion doctor-side LLM literature-search tool. Their own CEO publicly admitted: <em>"We're like 1 percent of the market in apps that think about hallucination as a limitation. 99 percent is still up for grabs."</em> For an unassuming physician trusting an AI's answer, a hallucinated guideline citation, a fabricated drug interaction, or a non-existent contraindication can directly cause a wrong diagnosis or a wrong prescription. The patient pays the cost.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">What is "deterministic" in computing?</p>
                <p className="text-sm text-slate-700">
                  A deterministic system, given the same input, <strong>always</strong> produces the same output. A calculator is deterministic. An LLM is not. Deterministic computing is reproducible, auditable, and testable — three things clinical regulators require.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">How P.AI Global Health (Patient AI) is deterministic from the ground up</p>
                <p className="text-sm text-slate-700">
                  Every clinical risk flag and SOAP item runs through <strong>deterministic rule engines</strong> written in plain Python. Same patient input → same output, every time. Every flag is traceable to a specific line of code, a clinical guideline, and the patient-text trigger that fired it. <strong>No large language model touches the clinical decision layer.</strong> LLMs in P.AI Global Health (Patient AI) are used only as bounded peripherals — translating Indic languages and structuring conversational chat — never to draft a diagnosis, never to invent a guideline, never to generate a SOAP from scratch.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">The benefit</p>
                <p className="text-sm text-slate-700">
                  The platform <strong>cannot hallucinate</strong> on the clinical decision layer because there is nothing in that layer capable of generating new content. It does not interpret the patient's history; it assembles it. Every line of the SOAP, every flag, every code is the result of a rule firing — the physician can audit any decision back to source.
                </p>
              </div>
            </div>
          </details>
        </section>

        {/* Instructions accordion (May 20, 2026) — field-by-field guidance for patients,
            collapsed by default, expands on tap. Same style as the AI Terminology accordion above. */}
        <section className="max-w-5xl mx-auto px-6 pt-3 pb-2" data-testid="intake-instructions-section">
          <details id="footer-instructions" className="group bg-slate-50 border-2 border-slate-300 rounded-2xl overflow-hidden">
            <summary
              className="cursor-pointer list-none px-5 py-3 flex items-center justify-between bg-white hover:bg-slate-50 select-none"
              data-testid="intake-instructions-toggle"
            >
              <span className="text-sm sm:text-base font-bold text-[#0369A1]">
                <span className="inline-block w-5 h-5 rounded-full bg-[#0369A1] text-white text-[11px] font-bold text-center leading-5 mr-2">?</span>
                Instructions to fill this form
                <span className="text-xs font-normal text-slate-500 ml-2">(tap for short explanations of each field)</span>
              </span>
              <span className="text-[#0369A1] group-open:rotate-180 transition-transform text-lg">▾</span>
            </summary>
            <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-2.5 border-t border-slate-200" data-testid="intake-instructions-body">
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Patient Name</span>
                <span className="text-sm text-slate-700">Your name. Optional.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Age &amp; Sex</span>
                <span className="text-sm text-slate-700">Your age (use months for infants) and sex assigned at birth.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Chief Complaint</span>
                <span className="text-sm text-slate-700">What is bothering you most today, in your own words.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Active Process</span>
                <span className="text-sm text-slate-700">Current conditions, ongoing issues, and lifelong allergies — one per line, with a date if you know it.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Inactive</span>
                <span className="text-sm text-slate-700">Past surgeries, resolved conditions, and old fractures — one per line, with a date if you know it.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Symptoms (OLDCARTS)</span>
                <span className="text-sm text-slate-700">When it started, where it is, how long it lasts, what it feels like, whether it spreads, what makes it better or worse, how often, and how bad (1–10).</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Vital Signs</span>
                <span className="text-sm text-slate-700">Temperature, blood pressure, heart rate, etc., if you have them. Optional.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Medications</span>
                <span className="text-sm text-slate-700">All medicines including over-the-counter, vitamins, supplements, and sleep aids — with dose, frequency, and start date.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Recent Labs</span>
                <span className="text-sm text-slate-700">Any recent lab results (e.g., bilirubin, INR, platelets). Optional.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Allergies</span>
                <span className="text-sm text-slate-700">Known reactions to medicines, foods, or the environment. Type "None known" if none.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Active Conditions</span>
                <span className="text-sm text-slate-700">Conditions a doctor currently manages. Tick the box if you are immunocompromised.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Past Medical History</span>
                <span className="text-sm text-slate-700">Resolved or inactive conditions. They appear in your record but won't trigger active alerts.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Family History</span>
                <span className="text-sm text-slate-700">Serious illnesses in parents, siblings, and grandparents.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Former Exposures</span>
                <span className="text-sm text-slate-700">Past smoking, alcohol, or substance use you have since stopped.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Additional Notes</span>
                <span className="text-sm text-slate-700">Anything else you want your care team to know.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Past Surgical History</span>
                <span className="text-sm text-slate-700">Any past operations — procedure, year, facility, and any complication.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Social Wellbeing</span>
                <span className="text-sm text-slate-700">A short, confidential question about feeling isolated (and recent loss) that helps your care team support you.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Care Team</span>
                <span className="text-sm text-slate-700">Your primary care physician and any specialists. Optional.</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-3">
                <span className="text-sm font-bold text-slate-900 sm:min-w-[180px]">Preferred Communication</span>
                <span className="text-sm text-slate-700">How you'd like to be contacted — text, voice, both, sign language, or AAC / pictogram.</span>
              </div>
            </div>
          </details>
        </section>

        {/* Physician-facing framing card — collapsed dropdown style (May 21, 2026).
            Same visual style as "Accessibility Support Available" and "How to Complete Your Intake"
            accordions above. Collapsed by default. Reduces noise on first impression for validators. */}
        {viewMode === 'physician' && (
          <section className="max-w-5xl mx-auto px-6 pt-3 pb-2" data-testid="physician-framing-section">
            <details id="footer-physicians" className="group bg-slate-50 border-2 border-slate-300 rounded-2xl overflow-hidden">
              <summary
                className="cursor-pointer list-none px-5 py-3 flex items-center justify-between bg-white hover:bg-slate-50 select-none"
                data-testid="physician-framing-toggle"
              >
                <span className="text-sm sm:text-base font-bold text-[#0369A1]">
                  <span className="inline-block w-5 h-5 rounded-full bg-[#0369A1] text-white text-[11px] font-bold text-center leading-5 mr-2">i</span>
                  For Physicians &amp; Providers — Patient voice → Structured clinical intelligence
                  <span className="text-xs font-normal text-slate-500 ml-2">(tap to expand)</span>
                </span>
                <span className="text-[#0369A1] group-open:rotate-180 transition-transform text-lg">▾</span>
              </summary>
              <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-slate-200" data-testid="physician-framing-card">
                <p className="text-sm text-slate-700 mb-2.5">
                  P.AI Global Health (Patient AI) does <strong>not</strong> interpret the patient's history. It <strong>assembles</strong> it. The patient brings together records from disparate sources — primary care, specialists, ER visits, foreign hospitals, pharmacies — that have likely never lived together inside any single EMR or EHR your practice can see. Under the <strong>21st Century Cures Act</strong>, the patient has the right to consolidate that record across providers. P.AI is where they do it.
                </p>
                <p className="text-sm text-slate-700 mb-2.5">
                  The output is a structured clinical brief in a SOAP format physicians already know how to read. It surfaces past clinical flags from sources your system never had access to — old allergies, prior surgical complications, medication interactions, missed screenings — so you can spend the visit on the <strong>chief complaint</strong> instead of reconstructing history.
                </p>
                <p className="text-sm text-slate-700 mb-3">
                  You choose the view: a <strong>chronological past-history timeline</strong> alone, or past history <strong>plus a draft SOAP</strong> intended for your review and sign-off. Nothing is invented. No large language model touches the clinical decision layer. The physician remains the sole diagnostic decision-maker.
                </p>
                <p className="text-xs italic text-slate-600 border-l-2 border-amber-400 pl-3" data-testid="sandbox-disclaimer">
                  <strong>Not HIPAA-compliant.</strong> This is a synthetic-data sandbox. The production architecture is designed to be HIPAA-Eligible.
                </p>
              </div>
            </details>
          </section>
        )}

          {/* PPHV accordion — relocated to LAST position in Information & Resources (UI reorder, content unchanged) */}
          <PPHVSection onOpenArchitecture={() => setShowArchitecture(true)} />
        </div>
      </section>

      <footer className="border-t border-slate-200 mt-2">
        <div className="max-w-4xl mx-auto px-6 py-10 text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-black mb-2">
            <span className="text-[#EF4444] font-extrabold">P.AI</span> Global Health — <span className="text-[#EF4444] font-extrabold">PATENT PENDING</span>
          </h3>
          <p className="text-sm text-black mb-4">Patient-centric multilingual agentic healthcare infrastructure</p>
          <a href="mailto:sadasivam.eniasivam@paiglobalhealth.com" className="text-[#2563EB] font-medium text-sm hover:underline">
            Contact: sadasivam.eniasivam@paiglobalhealth.com
          </a>
        </div>
        <div className="border-t border-slate-200 py-6 text-center">
          <p className="text-sm text-black mb-1">Synthetic Demo · No Real PHI · Provisional Patent Pending</p>
          <a href="https://paiglobalhealth.com" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] text-sm hover:underline block mb-3">paiglobalhealth.com</a>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => setShowAdmin(true)} className="text-sm text-[#2563EB]/60 hover:text-[#2563EB] transition-colors" data-testid="admin-entry">Admin / Maintenance</button>
            <span className="text-slate-300">|</span>
            <a href="/investor-access" className="text-sm text-[#2563EB]/60 hover:text-[#2563EB] transition-colors" data-testid="investor-entry">Investor Access</a>
            <span className="text-slate-300">|</span>
            <a href="/api/hub" target="_blank" rel="noopener noreferrer" className="text-sm text-[#7c3aed]/70 hover:text-[#7c3aed] transition-colors" data-testid="materials-hub-entry">Materials Hub</a>
            <span className="text-slate-300">|</span>
            <button onClick={() => setShowFounderPin(true)} className="text-sm text-slate-400 hover:text-slate-600 transition-colors" data-testid="founder-test-btn">
              {isFounder ? 'Founder Mode Active' : 'Founder Test Access'}
            </button>
            <span className="text-slate-300">|</span>
            <a href="/vault" className="text-sm text-slate-400 hover:text-slate-600 transition-colors" data-testid="vault-entry">Founder Vault</a>
            <span className="text-slate-300">|</span>
            <a href="/rounds" className="text-sm text-slate-400 hover:text-slate-600 transition-colors" data-testid="rounds-entry">Clinician Rounds</a>
            <span className="text-slate-300">|</span>
            <a href="/verify" className="text-sm text-slate-400 hover:text-slate-600 transition-colors" data-testid="verify-entry">Verify a Record</a>
            <span className="text-slate-300">|</span>
            <a href="/health" className="text-sm text-slate-400 hover:text-slate-600 transition-colors" data-testid="health-entry">System Health</a>
          </div>
        </div>
      </footer>

      {/* Retrieve Report Dialog */}
      <Dialog open={showRetrieve} onOpenChange={setShowRetrieve}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="retrieve-report-dialog">
          <DialogHeader><DialogTitle>Retrieve Report</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-4">
            <Input placeholder="Enter Encounter ID (e.g., PGH-000001)" value={retrieveId}
              onChange={e => setRetrieveId(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRetrieve()} data-testid="retrieve-id-input" />
            <Button onClick={handleRetrieve} disabled={retrieveLoading} className="bg-[#2563EB] hover:bg-blue-700" data-testid="retrieve-submit-btn">
              <Search className="w-4 h-4 mr-1" /> {retrieveLoading ? 'Loading...' : 'Retrieve'}
            </Button>
          </div>
          {retrievedReport && (
            <ScrollArea className="max-h-[50vh]">
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><strong>Encounter:</strong> {retrievedReport.encounterId}</div>
                  <div><strong>Date:</strong> {retrievedReport.visitDate?.slice(0,19)}</div>
                  <div><strong>Age:</strong> {retrievedReport.age}</div>
                  <div><strong>Sex:</strong> {retrievedReport.sex}</div>
                  <div className="col-span-2"><strong>Chief Complaint:</strong> {retrievedReport.chiefComplaint}</div>
                  <div className="col-span-2"><strong>Symptoms:</strong> {retrievedReport.symptoms}</div>
                  <div className="col-span-2"><strong>Medications:</strong> {retrievedReport.medications}</div>
                  <div className="col-span-2"><strong>Conditions:</strong> {retrievedReport.existingConditions}</div>
                </div>
                {retrievedReport.soap && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-semibold text-[#2563EB] mb-2">SOAP Note</h4>
                    <div className="space-y-2 text-xs">
                      <div><span className="font-semibold">CC:</span> {retrievedReport.soap.cc}</div>
                      <div><span className="font-semibold">Assessment:</span> {retrievedReport.soap.assessment?.substring(0, 300)}...</div>
                    </div>
                  </div>
                )}
                {retrievedReport.riskFlags?.length > 0 && (
                  <div className="mt-2">
                    <h4 className="font-semibold mb-1">Risk Flags</h4>
                    {retrievedReport.riskFlags.map((f, i) => (
                      <div key={i} className={`p-2 rounded mb-1 text-xs ${f.severity === 'CRITICAL' ? 'risk-critical' : f.severity === 'HIGH' ? 'risk-high' : 'risk-moderate'}`}>
                        [{f.severity}] {f.category}: {f.message}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => { const f=document.createElement('iframe'); f.style.display='none'; f.src=`${API}/encounter/${retrievedReport.encounterId}/patient-pdf`; document.body.appendChild(f); setTimeout(()=>document.body.removeChild(f),30000); }} data-testid="download-patient-pdf">Patient PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => { const f=document.createElement('iframe'); f.style.display='none'; f.src=`${API}/encounter/${retrievedReport.encounterId}/soap-pdf`; document.body.appendChild(f); setTimeout(()=>document.body.removeChild(f),30000); }} data-testid="download-soap-pdf">SOAP PDF</Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="history-dialog">
          <DialogHeader><DialogTitle>Encounter History</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {historyData.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No encounters recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {historyData.map((enc, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between" data-testid={`history-item-${i}`}>
                    <div>
                      <div className="font-semibold text-sm text-slate-900">{enc.encounterId}</div>
                      <div className="text-xs text-slate-500">{enc.visitDate?.slice(0,19)} | {enc.age}y {enc.sex} | {enc.chiefComplaint?.substring(0, 50)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {enc.riskFlags?.length > 0 && <Badge variant="destructive" className="text-[10px]">{enc.riskFlags.length} flags</Badge>}
                      <Badge variant="outline" className="text-[10px]">{enc.guardrailsStatus || 'N/A'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* System Architecture Modal */}
      <Dialog open={showArchitecture} onOpenChange={setShowArchitecture}>
        <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="architecture-dialog">
          <DialogHeader><DialogTitle className="text-2xl font-bold">How It Works</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            {/* For Patients: 4 Steps */}
            <h3 className="font-bold text-lg text-slate-900 mb-4">For Patients</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { step: '01', icon: <HeartPulse className="w-7 h-7" />, title: 'Describe Symptoms', desc: 'Use voice or text to explain how you feel' },
                { step: '02', icon: <Zap className="w-7 h-7" />, title: 'AI Structures Data', desc: 'The system organizes your information clinically' },
                { step: '03', icon: <ShieldCheck className="w-7 h-7" />, title: 'Doctor Reviews', desc: 'Physician receives structured SOAP note' },
                { step: '04', icon: <CheckCircle className="w-7 h-7" />, title: 'You Own Your Data', desc: 'Health record stays in your personal vault' },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 border border-slate-200 rounded-xl">
                  <div className="text-sm font-bold text-[#2563EB] mb-2">{item.step}</div>
                  <div className="w-12 h-12 rounded-full bg-sky-50 text-[#2563EB] flex items-center justify-center mx-auto mb-3">{item.icon}</div>
                  <h4 className="font-bold text-base text-slate-900 mb-1">{item.title}</h4>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Under the Hood */}
            <h3 className="font-bold text-lg text-slate-900 mb-2">Under the Hood</h3>
            <p className="text-sm text-slate-600 mb-5">
              P.AI uses a deterministic, auditable agent-style pipeline — not uncontrolled autonomous reasoning. Each step is visible, traceable, and designed for physician oversight.
            </p>

            {/* 7-Agent Pipeline */}
            <h3 className="font-bold text-lg text-slate-900 mb-4">7-Agent AI Pipeline</h3>
            <div className="space-y-3 mb-8">
              {[
                { num: '01', name: 'Intake Agent', desc: 'Validates, structures, and organizes raw patient input into a standardized format.' },
                { num: '02', name: 'Redactor Agent', desc: 'Scans for and redacts potential personally identifiable information (PII) patterns.' },
                { num: '03', name: 'Normalizer Agent', desc: 'Converts medical abbreviations and shorthand into standard clinical terminology.' },
                { num: '04', name: 'Longitudinal Memory Agent', desc: 'Loads prior encounter history to provide clinical context continuity.' },
                { num: '05', name: 'Draft Clinical Note Agent', desc: 'Generates a structured SOAP note with conservative clinical reasoning.' },
                { num: '06', name: 'Guardrails Agent', desc: 'Checks for definitive diagnosis language, hallucinated vitals, and safety concerns.' },
                { num: '07', name: 'Audit Log Agent', desc: 'Creates a complete audit trail of every pipeline step for traceability.' },
              ].map((agent, i) => (
                <div key={i} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <span className="text-xs font-bold text-[#2563EB] bg-blue-50 px-2 py-1 rounded">{agent.num}</span>
                  <div>
                    <span className="font-semibold text-sm text-slate-900">{agent.name}</span>
                    <p className="text-xs text-slate-600 mt-0.5">{agent.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing */}
            <div className="border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-600 mb-2">
                All outputs are drafts intended for physician review. The system is designed to structure patient information — not to replace clinical judgment.
              </p>
              <p className="text-xs text-slate-400">
                Clinical evidence framework: NICE/CKS, CDC, WHO, MedlinePlus/NLM. Conservative, rule-based logic only.
              </p>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Admin Dialog */}
      <FounderAdmin open={showAdmin} onClose={() => setShowAdmin(false)} onEditEncounter={handleEditIntake} />

      {/* Rule D — Physician Classification Decision Modal */}
      <Dialog open={showClassificationModal} onOpenChange={(o) => { if (!o) { setShowClassificationModal(false); setPendingClassificationData(null); } }}>
        <DialogContent className="max-w-xl" data-testid="physician-classification-dialog">
          <div className="p-2">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Clinical Classification Required</h3>
            <p className="text-xs text-slate-500 mb-4">PHYSICIAN ACTION NEEDED BEFORE PROCEEDING</p>
            {pendingClassificationData?.classificationBox && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                  <div>
                    <span className="font-semibold text-slate-700">Patient Intake Language:</span>
                    <div className="mt-0.5 p-2 bg-slate-50 rounded border border-slate-200" data-testid="cls-language">
                      {pendingClassificationData.classificationBox.patientIntakeLanguage}
                    </div>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-700">Input Modality:</span>
                    <div className="mt-0.5 p-2 bg-slate-50 rounded border border-slate-200" data-testid="cls-modality">
                      {pendingClassificationData.classificationBox.inputModality}
                    </div>
                  </div>
                </div>
                {pendingClassificationData.classificationBox.isVoice && (
                  <div className="mb-3 p-3 border-2 border-amber-400 bg-amber-50 rounded-md" data-testid="cls-voice-warning">
                    <p className="text-sm font-bold text-amber-900">⚠️ VOICE TRANSCRIPTION NOTICE</p>
                    <p className="text-xs text-amber-800 mt-1">
                      Patient completed intake using voice input in {pendingClassificationData.classificationBox.patientIntakeLanguage}. Transcript generated by speech-to-text. Please review for accuracy before confirming classification.
                    </p>
                  </div>
                )}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-slate-700 mb-1">Transcribed intake text:</p>
                  <pre className="text-xs p-2 bg-slate-50 border border-slate-200 rounded whitespace-pre-wrap max-h-32 overflow-y-auto" data-testid="cls-transcript">
                    {pendingClassificationData.classificationBox.transcribedText}
                  </pre>
                </div>
                {pendingClassificationData.classificationBox.isVoice && (
                  <div className="mb-3 text-xs">
                    <span className="font-semibold text-slate-700">Transcript accuracy confirmed?</span>
                    <div className="mt-1 flex gap-2">
                      <Button size="sm" variant={transcriptConfirmed === 'YES' ? 'default' : 'outline'} onClick={() => setTranscriptConfirmed('YES')} data-testid="cls-transcript-yes">YES</Button>
                      <Button size="sm" variant={transcriptConfirmed === 'NO' ? 'default' : 'outline'} onClick={() => setTranscriptConfirmed('NO')} className={transcriptConfirmed === 'NO' ? 'bg-red-600 hover:bg-red-700' : ''} data-testid="cls-transcript-no">NO</Button>
                    </div>
                  </div>
                )}
                <p className="text-sm font-semibold text-slate-800 mb-2">Please confirm:</p>
                {transcriptConfirmed === 'NO' ? (
                  <div className="p-3 border-2 border-red-300 bg-red-50 rounded text-sm text-red-800" data-testid="cls-transcript-rejected">
                    Transcript marked inaccurate. SOAP generation halted. Please ask the patient to re-enter by text or re-record voice. Close this dialog and resubmit.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => {
                        const fd = { ...pendingClassificationData.formData, physicianClassification: 'ACUTE', physicianTranscriptConfirmed: transcriptConfirmed };
                        setShowClassificationModal(false);
                        setPendingClassificationData(null);
                        handleSubmit(fd, pendingClassificationData.mode);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="cls-confirm-acute"
                    >
                      (1) NEW condition not previously diagnosed
                    </Button>
                    <Button
                      onClick={() => {
                        const fd = { ...pendingClassificationData.formData, physicianClassification: 'CHRONIC_MGMT', physicianTranscriptConfirmed: transcriptConfirmed };
                        setShowClassificationModal(false);
                        setPendingClassificationData(null);
                        handleSubmit(fd, pendingClassificationData.mode);
                      }}
                      variant="outline"
                      data-testid="cls-confirm-chronic"
                    >
                      (2) FOLLOW-UP for an existing condition
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Patient Agent Chat */}
      <AgentChat open={showAgent} onClose={() => setShowAgent(false)} encounterId={result?.encounterId || ''} />

      {/* Ambient Clinical Scribe */}
      <AmbientScribe open={showAmbientScribe} onClose={() => setShowAmbientScribe(false)} onResult={handleAmbientResult} />

      {/* Demo-mode-only Patient + Physician side-by-side mock view (May 26, 2026) */}
      <DemoSummaryModal open={showDemoSummary} onClose={() => setShowDemoSummary(false)} />

      {/* Investor Suite */}
      <InvestorSuite open={showInvestor} onClose={() => setShowInvestor(false)} />

      {/* Founder PIN Dialog */}
      <Dialog open={showFounderPin} onOpenChange={setShowFounderPin}>
        <DialogContent className="max-w-sm" data-testid="founder-pin-dialog">
          <div className="text-center p-4">
            <h3 className="font-bold text-lg mb-3">{isFounder ? 'Founder Mode Active' : 'Founder Test Access'}</h3>
            {isFounder ? (
              <p className="text-sm text-emerald-600 font-medium">Rate limits and session caps are bypassed for this session.</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Enter Founder PIN to bypass demo limits.</p>
                <Input
                  type="password"
                  value={founderPin}
                  onChange={e => setFounderPin(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { if (activateFounder(founderPin)) { setShowFounderPin(false); toast.success('Founder Mode activated'); } else { toast.error('Invalid PIN'); } }}}
                  className="border-2 border-slate-300 text-center"
                  data-testid="founder-pin-input"
                />
                <Button onClick={() => { if (activateFounder(founderPin)) { setShowFounderPin(false); toast.success('Founder Mode activated'); } else { toast.error('Invalid PIN'); }}} className="w-full bg-[#2563EB] hover:bg-blue-700">
                  Activate
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
